import { Component, Input, OnInit } from '@angular/core';
import { FormControlDefinition } from '../../../../shared/model/form-control-definition.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DetailFormDataModel } from '../../../../config/model/detail-form-data.model';
import { DetailViewNotifyService } from '../../shared/detailview-notify.service';
import { TranslateService } from '@ngx-translate/core';
import { TakeUntilDestroy } from '../../../../core/component/take-until-destroy';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-base-control',
  template: '',
  standalone: false,
})
export abstract class BaseControlComponent extends TakeUntilDestroy implements OnInit {
  @Input() prop: FormControlDefinition;
  @Input() viewExpanded: boolean;
  @Input() graphicFormGroup: FormGroup;
  @Input() detailFormDataModel: DetailFormDataModel;

  errorMessages: string[] = [];

  protected formField: FormControl;

  protected constructor(public detailViewNotifyService: DetailViewNotifyService, public translate: TranslateService) {
    super();
  }

  ngOnInit(): void {
    // create the form field
    this.formField = new FormControl();
    // set control value
    this.setInitialValue();

    // add new form field to form group
    this.graphicFormGroup.addControl(this.prop.field_name, this.formField);
    // compose validators
    const validators = this.initValidators(this.prop);
    // set validators on field
    this.formField.setValidators(Validators.compose(validators));
    // listen on validate event
    this.detailViewNotifyService.validateFeatureSubject.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.validateField();
    });
    this.formField.statusChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(state => {
      this.validateField();
    });

    this.detailViewNotifyService.onUpdatePatchGraphicFormValuesSubject
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.setInitialValue();
      });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  protected setInitialValue() {
    if (this.detailFormDataModel?.selectedFeature) {
      this.formField.setValue((this.detailFormDataModel?.selectedFeature.attributes)[this.prop.field_name]);
    }
  }

  /**
   * @name validateField
   * @description Validate fields on change and throw
   * proper error messages. This onChange Event-Listener method
   * will be called later than the binded Validator Object on the FormControl,
   * which makes it possible to check on the errors-array, because the error
   * values are already set.
   */
  private validateField() {
    this.errorMessages.length = 0;

    // if field validation has no errors
    if (!this.formField.errors) {
      return;
    }

    // required invalid
    if (this.formField.errors['required']) {
      // Initial invalid message
      this.errorMessages.push(this.translate.instant('DETAIL_VIEW.FORM_VALIDATION.REQUIRED'));
      return;
    }

    this.specificValidateField();

    // If we reach this point and no error messages means an error was unhandled
    if (!this.errorMessages.length) {
      throw new Error(`Not implemented error: ${JSON.stringify(this.formField.errors)}`);
    }
  }

  /**
   * @name specificValidateField
   * @description Add specific field validation. Called inside validateField()
   */
  protected specificValidateField() {}

  /**
   * @function initValidators
   * @param prop
   * @returns {Array<any>}
   * @description Compose validators by prop config
   */
  protected initValidators(prop): Array<any> {
    const validators = [];

    // Add validations common to all controls
    if (prop.field_required) validators.push(Validators.required);

    return validators;
  }
}
